/**
 * A labelled input, with its helper and its error.
 *
 * `design/DESIGN-SYSTEM.md` §2, verbatim: "15px 400, height 42px…, padding
 * 10px 14px, radius 10px, `--mdx-border`, white, placeholder `--mdx-slate`.
 * Focus: border `--mdx-brand` + `--mdx-focus-ring`, no default outline.
 * Error: `--mdx-border-error`, message beneath at 13px `--mdx-error` **stating
 * what to fix**. Label 14px 600 `--mdx-ink`, 6px above. Helper 13px
 * `--mdx-slate` beneath."
 *
 * The error lives on the FIELD and the message says what to fix, never what
 * went wrong — `auth-signup.html` renders "That email is already registered."
 * with "Log in instead?" beside it, and ARCH §4.3 requires the same of the API,
 * so the two agree by construction rather than by luck.
 *
 * `aria-describedby` binds the helper and the error to the input, and
 * `aria-invalid` marks it — a screen reader announces the fix along with the
 * field, which is the whole point of putting it there.
 *
 * Story 3.3.
 */
import { useId, type InputHTMLAttributes, type ReactNode, type Ref } from 'react'

export type FieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> & {
  /**
   * A `ReactNode` rather than a string, because a label is not always one
   * weight: `settings-credentials.html` draws "Base URL — optional" as 600 for
   * the name and 400 `--mdx-slate` for the qualifier, inside one `<label>`.
   * Widened by Story 11.2; every existing caller passes a string and is
   * unaffected.
   */
  label: ReactNode
  helper?: ReactNode
  error?: ReactNode
  /**
   * Sits on the label's baseline, right-aligned — `auth-login.html`'s "Forgot
   * your password?". Its note says why it lives there: "Forgot your password
   * sits above the field it belongs to, where a stuck person is already
   * looking."
   */
  action?: ReactNode
  /**
   * A ref onto the INPUT, not onto the wrapper.
   *
   * Named `inputRef` rather than `ref` deliberately. On React 18 a plain `ref`
   * on a function component needs `forwardRef`, and no control in `ui/` uses it
   * — adding one here would make this the odd component out and would put the
   * ref on the wrapper for anybody who then copied the pattern. The field owns
   * its `<input>`'s id (it is generated by `useId`, so a caller cannot target
   * it), so an explicit prop is the only honest way to reach it.
   *
   * Story 11.2 needs it because "Replace" and the empty state's "Add a key"
   * both put the cursor in the API key field.
   */
  inputRef?: Ref<HTMLInputElement>
  /**
   * A unit drawn inside the field, before the value — the `$` on
   * `usage-cost.html`'s two budget caps (Story 23.4): 15px `--mdx-slate`, 14px in
   * from the edge, over a left padding that widens to 28px so a typed value never
   * runs under it. Hidden from assistive technology, which hears it as part of the
   * label instead: a bare "$" read out before the field says nothing.
   */
  prefix?: string
}

export function Field({ label, helper, error, action, inputRef, prefix, className = '', ...rest }: FieldProps) {
  const id = useId()
  const helperId = `${id}-helper`
  const errorId = `${id}-error`
  const describedBy = [helper ? helperId : null, error ? errorId : null].filter(Boolean).join(' ')

  const input = (
    <input
      id={id}
      ref={inputRef}
      {...rest}
      aria-invalid={error ? true : undefined}
      aria-describedby={describedBy || undefined}
      className={[
        'h-mdx-input w-full min-w-0 rounded-mdx-control',
        prefix ? 'py-mdx-10 pl-mdx-28 pr-mdx-14' : 'px-mdx-14 py-mdx-10',
        'text-mdx-15 font-mdx-regular',
        'bg-mdx-white text-mdx-ink placeholder:text-mdx-slate',
        'border outline-none transition-colors duration-mdx-swift ease-mdx-swift',
        error ? 'border-mdx-error' : 'border-mdx-line focus:border-mdx-brand',
        'focus:[box-shadow:var(--mdx-focus-ring)]',
        'disabled:bg-mdx-ground disabled:text-mdx-slate',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    />
  )

  return (
    <div className="flex flex-col gap-mdx-6">
      {action ? (
        /**
         * WRAPS, and the action wraps WHOLE — `auth-login.html`'s code step draws
         * the row `flex-wrap`, 2px between lines, with the link `nowrap` and
         * pushed right. So when the two do not fit, the link drops to its own
         * line intact, instead of both breaking mid-phrase: "Use your
         * authenticator app instead" at 375px (Story 24.2). A row that fits —
         * every "Forgot your password?" — is unchanged.
         */
        <div className="flex flex-wrap items-baseline justify-between gap-x-mdx-16 gap-y-mdx-2">
          <label htmlFor={id} className="text-mdx-14 font-mdx-semibold text-mdx-ink">
            {label}
            {prefix ? <span className="sr-only"> ({prefix})</span> : null}
          </label>
          <span className="ml-auto whitespace-nowrap">{action}</span>
        </div>
      ) : (
        <label htmlFor={id} className="text-mdx-14 font-mdx-semibold text-mdx-ink">
          {label}
          {prefix ? <span className="sr-only"> ({prefix})</span> : null}
        </label>
      )}
      {prefix ? (
        <span className="relative flex">
          <span
            aria-hidden="true"
            className="pointer-events-none absolute left-mdx-14 top-mdx-10 text-mdx-15 text-mdx-slate"
          >
            {prefix}
          </span>
          {input}
        </span>
      ) : (
        input
      )}
      {helper ? (
        <span id={helperId} className="text-mdx-13 text-mdx-slate">
          {helper}
        </span>
      ) : null}
      {error ? (
        <span id={errorId} className="text-mdx-13 text-mdx-error">
          {error}
        </span>
      ) : null}
    </div>
  )
}
